import React, { useState } from 'react';
import { X, AlertTriangle, Volume2, Video, FileText, AlertCircle, WifiOff, Trash, CheckCircle } from 'lucide-react';
import { API_CONFIG } from './config/apiConfig';

const ReportIssueModal = ({ movie, onClose, currentTime }) => {
    const [issueType, setIssueType] = useState('');
    const [description, setDescription] = useState('');
    const [includeTimestamp, setIncludeTimestamp] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'

    const issueTypes = [
        {
            value: 'audio_problem',
            label: 'Problème de son',
            icon: Volume2,
            description: 'Pas de son, décalage audio, son faible...'
        },
        {
            value: 'video_problem',
            label: "Problème d'image",
            icon: Video,
            description: 'Image figée, coupure, artefacts...'
        },
        {
            value: 'subtitle_problem',
            label: 'Problème de sous-titres',
            icon: FileText,
            description: 'Sous-titres absents, décalés, mal traduits...'
        },
        {
            value: 'incomplete',
            label: 'Contenu incomplet',
            icon: AlertCircle,
            description: 'Film coupé, épisode tronqué...'
        },
        {
            value: 'wrong_content',
            label: 'Mauvais contenu',
            icon: Trash,
            description: 'Ce n\'est pas le bon film/épisode'
        },
        {
            value: 'quality_poor',
            label: 'Qualité médiocre',
            icon: Video,
            description: 'Qualité très basse, pixellisé...'
        },
        {
            value: 'buffering',
            label: 'Buffering excessif',
            icon: WifiOff,
            description: 'Chargement trop lent, coupures fréquentes'
        },
        {
            value: 'other',
            label: 'Autre problème',
            icon: AlertTriangle,
            description: 'Un autre type de problème'
        }
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!issueType) {
            alert('Veuillez sélectionner un type de problème');
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const token = localStorage.getItem('cinestream_token');

            // Récupérer user_id et username depuis localStorage
            const userString = localStorage.getItem('cinestream_user');
            const user = userString ? JSON.parse(userString) : null;

            if (!user || !user.id || !user.username) {
                alert('Erreur: Utilisateur non authentifié');
                setIsSubmitting(false);
                return;
            }

            // API Laravel
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/reports`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Admin-Token': token || ''
                },
                body: JSON.stringify({
                    user_id: user.id,
                    username: user.username,
                    movie_id: movie.id,
                    issue_type: issueType,
                    description: description,
                    timestamp_in_video: includeTimestamp ? Math.floor(currentTime) : null
                })
            });

            const data = await response.json();

            if (data.success) {
                setSubmitStatus('success');
                setTimeout(() => {
                    onClose();
                }, 2000);
            } else {
                setSubmitStatus('error');
                alert('Erreur: ' + (data.message || 'Une erreur est survenue'));
            }
        } catch (error) {
            console.error('Erreur lors du signalement:', error);
            setSubmitStatus('error');
            alert('Erreur lors de l\'envoi du signalement');
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatTime = (seconds) => {
        if (!seconds) return '0:00';
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = Math.floor(seconds % 60);
        if (h > 0) {
            return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
        }
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    const overlayStyle = {
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 50, padding: 16, overflowY: 'auto',
    };

    if (submitStatus === 'success') {
        return (
            <div style={overlayStyle}>
                <div style={{
                    background: 'var(--bg-modal)',
                    border: '1px solid rgba(34,197,94,0.3)',
                    borderRadius: 'var(--r-2xl)',
                    padding: 40, maxWidth: 400, width: '100%',
                    textAlign: 'center',
                    animation: 'scaleIn 0.2s ease-out',
                }}>
                    <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(34,197,94,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                        <CheckCircle style={{ width: 32, height: 32, color: '#22C55E' }} />
                    </div>
                    <h3 style={{ color: 'var(--text-1)', fontWeight: 700, fontSize: '1.25rem', marginBottom: 8 }}>Signalement envoyé !</h3>
                    <p style={{ color: 'var(--text-4)', fontSize: '0.9375rem' }}>
                        Merci pour votre retour. Notre équipe va examiner le problème.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div style={overlayStyle}>
            <div style={{
                background: 'var(--bg-modal)',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--r-2xl)',
                boxShadow: 'var(--shadow-xl)',
                padding: 24, maxWidth: 640, width: '100%',
                maxHeight: '90vh', overflowY: 'auto',
                animation: 'scaleIn 0.2s ease-out',
            }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{ width: 46, height: 46, borderRadius: 'var(--r-lg)', background: 'rgba(239,68,68,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <AlertTriangle style={{ width: 22, height: 22, color: '#EF4444' }} />
                        </div>
                        <div>
                            <h2 style={{ color: 'var(--text-1)', fontWeight: 700, fontSize: '1.25rem' }}>Signaler un problème</h2>
                            <p style={{ color: 'var(--text-4)', fontSize: '0.875rem', marginTop: 2 }}>
                                {movie.title}{movie.season && movie.episode && ` - S${movie.season}E${movie.episode}`}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{ width: 36, height: 36, borderRadius: 'var(--r-md)', background: 'transparent', border: 'none', color: 'var(--text-4)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 150ms ease, color 150ms ease' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--glass-hover)'; e.currentTarget.style.color = 'var(--text-1)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-4)'; }}
                    >
                        <X style={{ width: 18, height: 18 }} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {/* Issue types */}
                    <div>
                        <label style={{ display: 'block', color: 'var(--text-1)', fontWeight: 600, fontSize: '0.9375rem', marginBottom: 12 }}>
                            Type de problème *
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: 10 }}>
                            {issueTypes.map((type) => {
                                const Icon = type.icon;
                                const selected = issueType === type.value;
                                return (
                                    <button
                                        key={type.value}
                                        type="button"
                                        onClick={() => setIssueType(type.value)}
                                        style={{
                                            textAlign: 'left',
                                            padding: '12px 14px',
                                            borderRadius: 'var(--r-lg)',
                                            border: `1px solid ${selected ? 'rgba(239,68,68,0.5)' : 'var(--glass-border)'}`,
                                            background: selected ? 'rgba(239,68,68,0.08)' : 'var(--glass)',
                                            cursor: 'pointer',
                                            transition: 'border-color 150ms ease, background 150ms ease',
                                        }}
                                        onMouseEnter={e => { if (!selected) e.currentTarget.style.background = 'var(--glass-hover)'; }}
                                        onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'var(--glass)'; }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                                            <div style={{
                                                width: 34, height: 34, borderRadius: 'var(--r-md)', flexShrink: 0,
                                                background: selected ? 'rgba(239,68,68,0.15)' : 'var(--bg-elevated)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                <Icon style={{ width: 16, height: 16, color: selected ? '#EF4444' : 'var(--text-4)' }} />
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <p style={{ color: selected ? 'var(--text-1)' : 'var(--text-2)', fontWeight: 600, fontSize: '0.875rem', marginBottom: 2 }}>{type.label}</p>
                                                <p style={{ color: 'var(--text-4)', fontSize: '0.75rem', lineHeight: 1.4 }}>{type.description}</p>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Timestamp */}
                    {currentTime > 0 && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--glass)', border: '1px solid var(--glass-border)', borderRadius: 'var(--r-lg)', padding: '12px 16px', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={includeTimestamp}
                                onChange={(e) => setIncludeTimestamp(e.target.checked)}
                                style={{ width: 18, height: 18, accentColor: '#EF4444', cursor: 'pointer' }}
                            />
                            <div style={{ flex: 1 }}>
                                <span style={{ color: 'var(--text-1)', fontWeight: 600, fontSize: '0.9375rem' }}>Inclure la position dans la vidéo</span>
                                <p style={{ color: 'var(--text-4)', fontSize: '0.8125rem', marginTop: 2 }}>Position actuelle : {formatTime(currentTime)}</p>
                            </div>
                        </label>
                    )}

                    {/* Description */}
                    <div>
                        <label style={{ display: 'block', color: 'var(--text-1)', fontWeight: 600, fontSize: '0.9375rem', marginBottom: 8 }}>Description (optionnel)</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Décrivez le problème en détail..."
                            rows={4}
                            style={{
                                width: '100%', background: 'var(--glass)', border: '1px solid var(--glass-border)',
                                borderRadius: 'var(--r-lg)', padding: '12px 14px', color: 'var(--text-1)',
                                fontSize: '0.9375rem', outline: 'none', resize: 'none', boxSizing: 'border-box',
                                transition: 'border-color 200ms ease, box-shadow 200ms ease',
                            }}
                            onFocus={e => { e.currentTarget.style.borderColor = 'rgba(239,68,68,0.5)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(239,68,68,0.1)'; }}
                            onBlur={e => { e.currentTarget.style.borderColor = 'var(--glass-border)'; e.currentTarget.style.boxShadow = 'none'; }}
                        />
                        <p style={{ color: 'var(--text-4)', fontSize: '0.75rem', marginTop: 6 }}>
                            Plus de détails nous aideront à résoudre le problème plus rapidement.
                        </p>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row" style={{ gap: 10, paddingTop: 4 }}>
                        <button type="button" onClick={onClose} className="btn-ghost" style={{ flex: 1, padding: '12px' }}>Annuler</button>
                        <button
                            type="submit"
                            disabled={!issueType || isSubmitting}
                            style={{
                                flex: 1, padding: '12px', borderRadius: 'var(--r-md)',
                                background: !issueType || isSubmitting ? 'var(--glass)' : 'linear-gradient(135deg, #EF4444, var(--pink))',
                                border: '1px solid ' + (!issueType || isSubmitting ? 'var(--glass-border)' : 'transparent'),
                                color: !issueType || isSubmitting ? 'var(--text-4)' : '#fff',
                                fontWeight: 700, fontSize: '0.9375rem',
                                cursor: !issueType || isSubmitting ? 'not-allowed' : 'pointer',
                                transition: 'background 200ms ease, color 200ms ease',
                            }}
                        >
                            {isSubmitting ? 'Envoi...' : 'Envoyer le signalement'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ReportIssueModal;